import axios from 'axios'
import { v4 } from 'uuid'

import {
  abortMultipartUpload,
  callPresignedUrl,
  completeMultipartUpload,
  createPresignedUrl,
  initiateMultipartUpload,
  validateUploadId,
} from 'api/storage'
import constants from '../../../../constants'
import i18n from '../../../../i18n'
import { getMediaDimensions } from 'utils/helpers'
import { insertMediaItems } from './mediaPayload'
import { saveLibraryImages } from 'api/library'

const PART_SIZE = 20 * 1000 * 1000 //20MB

const buildReplaceFilePayload = (file, fileToReplace) => ({
  file,
  fileToReplace,
})

const parseReplaceFileAction = (action, state) => {
  const payload = action.payload

  if (payload?.fileToReplace && payload?.file) {
    return {
      newFile: payload.file,
      fileToReplace: payload.fileToReplace,
    }
  }

  return {
    newFile: payload,
    fileToReplace: state.fileToReplace,
  }
}

const isFileInCoCreateGroup = (file, groupId) =>
  !!file && file.sub_group_id === groupId

const shouldTrackReplaceRollback = (newFile) =>
  newFile?.status === fileStatus.new || newFile?.status === fileStatus.uploading

const pruneFailedReplacesByMedia = (failedReplaceByNewId, media) => {
  const mediaIds = new Set((media ?? []).map((file) => String(file.id)))

  return Object.fromEntries(
    Object.entries(failedReplaceByNewId ?? {}).filter(([newFileId, entry]) =>
      mediaIds.has(String(newFileId)) || mediaIds.has(String(entry?.oldFile?.id)),
    ),
  )
}

const removeFailedReplaceEntriesByOldId = (failedReplaceByNewId, oldFileId) =>
  Object.fromEntries(
    Object.entries(failedReplaceByNewId ?? {}).filter(([, entry]) => entry?.oldFile?.id !== oldFileId),
  )

export const MEDIA_ASPECT_RATIOS = ['1:1', '4:5', '1.91:1', '9:16']
export const MEDIA_SLOT_RATIOS = ['1:1', '9:16']

const REEL_PLACEMENT_THRESHOLD = 1.4

const parseRatioLabel = (ratioLabel) => {
  const [widthPart, heightPart] = ratioLabel.split(':').map(Number)

  if (!widthPart || !heightPart) {
    return null
  }

  return widthPart / heightPart
}

const findClosestRatioLabel = (width, height, ratioLabels) => {
  if (!width || !height) {
    return null
  }

  const aspectRatio = width / height

  return ratioLabels.reduce((closestRatio, ratioLabel) => {
    const targetRatio = parseRatioLabel(ratioLabel)
    const closestTargetRatio = parseRatioLabel(closestRatio)

    if (targetRatio == null || closestTargetRatio == null) {
      return closestRatio
    }

    return Math.abs(aspectRatio - targetRatio) < Math.abs(aspectRatio - closestTargetRatio)
      ? ratioLabel
      : closestRatio
  }, ratioLabels[0])
}

export const inferPlacementType = (file) => {
  if (!file?.width || !file?.height) {
    return null
  }

  const aspectRatio = file.height / file.width
  return aspectRatio > REEL_PLACEMENT_THRESHOLD ? 'reel' : 'feed'
}

export const computeAspectRatio = (width, height) =>
  findClosestRatioLabel(width, height, MEDIA_ASPECT_RATIOS)

export const computeSlotRatio = (width, height) => {
  const placementType = inferPlacementType({ width, height })
  return placementType === 'reel' ? '9:16' : '1:1'
}

export const aspectRatioLabelToSlotRatio = (ratioLabel) =>
  ratioLabel === '9:16' ? '9:16' : '1:1'

export const resolveImageSlotRatio = (image) => {
  if (!image) {
    return null
  }

  if (image.placement_type === 'reel') {
    return '9:16'
  }

  if (image.placement_type === 'feed') {
    return '1:1'
  }

  if (image.width && image.height) {
    return computeSlotRatio(image.width, image.height)
  }

  if (image.ratio) {
    return aspectRatioLabelToSlotRatio(image.ratio)
  }

  return null
}

export const slotRatioToPlacementType = (slotRatio) =>
  slotRatio === '9:16' ? 'reel' : 'feed'

export const validatePlacementForSlot = (width, height, requiredSlotRatio) => {
  if (!width || !height || !requiredSlotRatio) {
    return false
  }

  const uploadedPlacementType = inferPlacementType({ width, height })
  const requiredPlacementType = slotRatioToPlacementType(requiredSlotRatio)

  return uploadedPlacementType === requiredPlacementType
}

export const buildMediaSlots = (images = [], { excludeRatio = null } = {}) => {
  const imagesBySlot = new Map()

  images.forEach((image) => {
    const slotRatio = resolveImageSlotRatio(image)

    if (slotRatio && !imagesBySlot.has(slotRatio)) {
      imagesBySlot.set(slotRatio, image)
    }
  })

  const excludeSlotRatio = excludeRatio ? aspectRatioLabelToSlotRatio(excludeRatio) : null
  const slotRatios = excludeSlotRatio
    ? MEDIA_SLOT_RATIOS.filter((ratio) => ratio !== excludeSlotRatio)
    : MEDIA_SLOT_RATIOS

  return slotRatios.map((ratio) => ({
    ratio,
    image: imagesBySlot.get(ratio) ?? null,
  }))
}

export const fileStatus = Object.freeze({
  new: 'NEW',
  uploading: 'UPLOADING',
  success: 'SUCCESS',
  uploadFailed: 'UPLOAD_FAILED',
  invalid: 'INVALID',
})

export const initialState = {
  media: [],
  isLoading: false,
  success: null,
  error: null,
  valid: null,
  fileToReplace: null,
  failedReplaceByNewId: {},
  generatedMediaCount: 0,
  eventId: null,
  tc: null,
  tcRunId: null,
}

export const actions = Object.freeze({
  addFile: 'ADD_FILE',
  addLibraryMedia: 'ADD_LIBRARY_MEDIA',
  removeFile: 'REMOVE_FILE',
  startReplace: 'START_REPLACE',
  resetReplace: 'RESET_REPLACE',
  replaceFile: 'REPLACE_FILE',
  cancelReplace: 'CANCEL_REPLACE',
  editFileInfo: 'EDIT_FILE_INFO',

  fileUploadInitiate: 'FILE_UPLOAD_INITIATE',
  fileUploadFail: 'FILE_UPLOAD_FAIL',
  fileUploadSuccess: 'FILE_UPLOAD_SUCCESS',
  uploadComplete: 'UPLOAD_COMPLETE',

  addFilesAsync: 'ADD_FILE_ASYNC',
  uploadFilesAsync: 'UPLOAD_FILES_ASYNC',
  uploadCompleteAsync: 'UPLOAD_COMPLETE_ASYNC',
  removeFileAsync: 'REMOVE_FILE_ASYNC',
  replaceFileAsync: 'REPLACE_FILE_ASYNC',
  retryUploadAsync: 'RETRY_UPLOAD_ASYNC',
  addFileToGroupAsync: 'ADD_FILE_TO_GROUP_ASYNC',
  replaceFileInGroupAsync: 'REPLACE_FILE_IN_GROUP_ASYNC',
  removeGroupAsync: 'REMOVE_GROUP_ASYNC',
  removeFromGroupAsync: 'REMOVE_FROM_GROUP_ASYNC',
  removeFiles: 'REMOVE_FILES',
  removeMediaGroup: 'REMOVE_MEDIA_GROUP',
  removeMediaGroupAsync: 'REMOVE_MEDIA_GROUP_ASYNC',
  addFileToCoCreateGroupAsync: 'ADD_FILE_TO_COCREATE_GROUP_ASYNC',
  replaceFileInCoCreateGroupAsync: 'REPLACE_FILE_IN_COCREATE_GROUP_ASYNC',
  removeFileFromCoCreateGroupAsync: 'REMOVE_FILE_FROM_COCREATE_GROUP_ASYNC',
  applyGeneratedVariants: 'APPLY_GENERATED_VARIANTS',
  setMediaGenerating: 'SET_MEDIA_GENERATING',
  clearMediaGenerating: 'CLEAR_MEDIA_GENERATING',
})

export function mediaUploadReducer(state, action) {
  switch (action.type) {
    case actions.addFile: {
      const existingMedia = state.media.filter((x) => x.status !== fileStatus.invalid)
      const newMedia = insertMediaItems(existingMedia, action.payload)

      return {
        ...state,
        media: newMedia,
        error: null,
        valid: newMedia.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.addLibraryMedia: {
      const existingMedia = state.media
      const newMedia = insertMediaItems(existingMedia, action.payload)

      return {
        ...state,
        media: newMedia,
        error: null,
        valid: newMedia.every(
          (x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed,
        ),
      }
    }
    case actions.removeFile: {
      const fileToRemove = action.payload
      const failedReplaceEntry = state.failedReplaceByNewId?.[fileToRemove.id]

      if (failedReplaceEntry && fileToRemove.status === fileStatus.uploadFailed) {
        const { oldFile } = failedReplaceEntry
        URL.revokeObjectURL(fileToRemove?.preview)

        const media = state.media.map((file) =>
          file.id === fileToRemove.id ? oldFile : file,
        )

        const { [fileToRemove.id]: removedEntry, ...restFailedReplaces } = state.failedReplaceByNewId ?? {}
        void removedEntry

        return {
          ...state,
          media,
          failedReplaceByNewId: pruneFailedReplacesByMedia(restFailedReplaces, media),
          fileToReplace: null,
          error: null,
          valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
        }
      }

      URL.revokeObjectURL(fileToRemove?.preview)

      const media = state.media.filter((x) => x.id !== fileToRemove.id)

      return {
        ...state,
        media,
        failedReplaceByNewId: pruneFailedReplacesByMedia(state.failedReplaceByNewId, media),
        error: null,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.removeFiles: {
      const filesToRemove = Array.isArray(action.payload) ? action.payload : [action.payload]

      filesToRemove.forEach((file) => {
        URL.revokeObjectURL(file?.preview)
      })

      const fileIdsToRemove = new Set(filesToRemove.map((f) => f.id))
      const media = state.media.filter((x) => !fileIdsToRemove.has(x.id))

      return {
        ...state,
        media,
        failedReplaceByNewId: pruneFailedReplacesByMedia(state.failedReplaceByNewId, media),
        error: null,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.applyGeneratedVariants: {
      const media = action.payload

      return {
        ...state,
        media,
        generatedMediaCount: 0,
        failedReplaceByNewId: pruneFailedReplacesByMedia(state.failedReplaceByNewId, media),
        error: null,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.removeMediaGroup: {
      const { groupId } = action.payload
      const filesToRemove = state.media.filter((file) => isFileInCoCreateGroup(file, groupId))

      filesToRemove.forEach((file) => {
        URL.revokeObjectURL(file?.preview)
      })

      const media = state.media.filter((file) => !isFileInCoCreateGroup(file, groupId))

      return {
        ...state,
        media,
        error: null,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.startReplace: {
      const fileToReplace = action.payload
      if (!fileToReplace) throw new Error('Invalid file to replace')

      return {
        ...state,
        fileToReplace,
        error: null,
      }
    }
    case actions.cancelReplace: {
      return {
        ...state,
        fileToReplace: null,
      }
    }
    case actions.resetReplace: {
      const { fileToReplace } = state
      if (!fileToReplace) {
        return {
          ...state,
          error: action.payload?.error,
        }
      }

      const media = state.media.map((file) =>
        file.id === fileToReplace.id ? fileToReplace : file,
      )

      return {
        ...state,
        media,
        fileToReplace: null,
        failedReplaceByNewId: removeFailedReplaceEntriesByOldId(
          state.failedReplaceByNewId,
          fileToReplace.id,
        ),
        error: action.payload?.error,
        valid:
          media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed) &&
          !action.payload?.error,
      }
    }
    case actions.replaceFile: {
      const { newFile, fileToReplace } = parseReplaceFileAction(action, state)

      if (!fileToReplace) throw new Error('Invalid file ID')

      const media = state.media.map((file) =>
        file.id === fileToReplace.id ? newFile : file,
      )

      const staleFailedReplaces = removeFailedReplaceEntriesByOldId(
        state.failedReplaceByNewId,
        fileToReplace.id,
      )

      const failedReplaceByNewId = shouldTrackReplaceRollback(newFile)
        ? {
          ...staleFailedReplaces,
          [newFile.id]: { oldFile: fileToReplace },
        }
        : staleFailedReplaces

      return {
        ...state,
        media,
        failedReplaceByNewId,
        error: null,
        valid: media.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed),
      }
    }
    case actions.editFileInfo: {
      const media = state.media
      const { id: fileId } = action.payload
      const file = media.find((x) => x.id === fileId)

      if (!file) throw new Error('Invalid file ID')

      const newMedia = constructEditObject(media, action.payload)
      const valid = newMedia.every((x) => x.status !== fileStatus.invalid && x.status !== fileStatus.uploadFailed)

      return {
        ...state,
        media: newMedia,
        valid,
      }
    }
    case actions.setMediaGenerating: {
      const { id, count } = action.payload || {}
      if (!id) return state

      return {
        ...state,
        media: state.media.map((file) =>
          String(file.id) === String(id) ? { ...file, generating: true } : file,
        ),
        generatedMediaCount: count ?? state.generatedMediaCount,
      }
    }
    case actions.clearMediaGenerating: {
      return {
        ...state,
        media: state.media.map((file) =>
          file.generating ? { ...file, generating: false } : file,
        ),
        generatedMediaCount: 0,
      }
    }
    case actions.fileUploadInitiate: {
      const newFiles = state.media.map((file) => {
        if (file.id === action.payload.id) {
          return { ...file, ...action.payload, status: fileStatus.uploading, error: '' }
        }
        return file
      })

      return {
        ...state,
        media: newFiles,
        isLoading: true,
      }
    }
    case actions.fileUploadFail: {
      const newFiles = state.media.map((file) => {
        if (file.id === action.payload.id) {
          let newFile = file

          return { ...newFile, status: fileStatus.uploadFailed, error: i18n.t('Uploads.errors.fileUploadFailed') }
        }
        return file
      })
      return {
        ...state,
        media: newFiles,
        isLoading: false,
        success: false,
        error: i18n.t('Uploads.errors.uploadFailed'),
      }
    }
    case actions.fileUploadSuccess: {
      const uploadedFileId = action.payload.id

      const newFiles = state.media.map((file) => {
        if (file.id === uploadedFileId) {
          return { ...file, status: fileStatus.success, error: '' }
        }
        return file
      })

      let nextState = {
        ...state,
        media: newFiles,
        failedReplaceByNewId: (() => {
          const { [uploadedFileId]: removedEntry, ...restFailedReplaces } = state.failedReplaceByNewId ?? {}
          void removedEntry
          return restFailedReplaces
        })(),
      }

      // if we have a file to replace, we need to clear it after the upload is successful
      if (state.fileToReplace && uploadedFileId === state.fileToReplace.id) {
        URL.revokeObjectURL(state.fileToReplace?.preview)
        nextState = {
          ...nextState,
          fileToReplace: null,
        }
      }

      return nextState
    }
    default:
      throw new Error(`Unknown action type: ${action.type}`)
  }
}

export const asyncActionHandlers = {
  [actions.addFilesAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { tc, tcRunId, eventId } = getState()
        const media = [action.payload].flat()

        const validationResult = await Promise.all(media.map((file) => validateFileAsync(file)))

        const newFiles = await Promise.all(
          validationResult.map((result) => {
            const [file, error] = result
            return constructFileObject(file, error)
          }),
        )

        dispatch({ type: actions.addFile, payload: newFiles })

        if (newFiles.every((x) => x.status === fileStatus.invalid)) return

        const filesToUpload = newFiles.filter((x) => x.status === fileStatus.new)

        await Promise.all(filesToUpload.map((file) => uploadFile(dispatch, file, { tc, tcRunId, eventId })))
      },
  [actions.removeFileAsync]:
    ({ dispatch }) =>
      async (action) => {
        const file = action.payload

        abortUpload(file)
        dispatch({ type: actions.removeFile, payload: file })
      },
  [actions.replaceFileAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const state = getState()
        const { tc, tcRunId, eventId } = state
        const fileToReplace = state.fileToReplace
        if (!fileToReplace) throw new Error('Missing or invalid fileToReplace')

        const newFile = [action.payload].flat()[0]
        const [file, error] = await validateFileAsync(newFile)

        if (error) {
          dispatch({ type: actions.resetReplace, payload: { error } })
          return
        }

        const fileToUpload = await constructFileObject(file, error)

        if (fileToReplace.placement_type && fileToUpload.placement_type) {
          if (fileToReplace.placement_type !== fileToUpload.placement_type) {
            const errorKey = fileToReplace.placement_type === 'feed'
              ? 'Recommendations.campaign.setup.media.errors.feedReplace'
              : 'Recommendations.campaign.setup.media.errors.reelReplace'

            dispatch({ type: actions.resetReplace, payload: { error: i18n.t(errorKey) } })
            return
          }
        }

        const preparedFile = {
          ...fileToUpload,
          sub_group_id: fileToReplace.sub_group_id ?? null,
          headline: null,
          is_generated: false,
        }

        dispatch({
          type: actions.replaceFile,
          payload: buildReplaceFilePayload(preparedFile, fileToReplace),
        })

        try {
          await uploadFile(dispatch, preparedFile, { tc, tcRunId, eventId })
        } catch (error) {
          dispatch({
            type: actions.resetReplace,
            payload: { error: error?.message || 'File upload failed' },
          })
          return
        }
      },
  [actions.retryUploadAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { tc, tcRunId, eventId } = getState()
        const file = action.payload
        await uploadFile(dispatch, file, { tc, tcRunId, eventId })
      },
  [actions.addFileToGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { file, targetFileId, requiredPlacementType } = action.payload
        const state = getState()

        // Find target file
        const targetFile = state.media.find((x) => x.id === targetFileId)
        if (!targetFile) throw new Error('Target file not found')

        // Validate file
        const [validatedFile, error] = await validateFileAsync(file)
        if (error) {
          throw new Error(error)
        }

        // Create file object (this will calculate placement_type)
        const fileToUpload = await constructFileObject(validatedFile, error)

        if (fileToUpload.placement_type !== requiredPlacementType) {
          throw new Error(`File aspect ratio doesn't match required placement type. Expected: ${requiredPlacementType}, got: ${fileToUpload.placement_type}`)
        }

        fileToUpload.insertAfterId = targetFileId

        dispatch({ type: actions.addFile, payload: [fileToUpload] })

        // Upload the new file
        await uploadFile(dispatch, fileToUpload, { tc: state.tc, tcRunId: state.tcRunId, eventId: state.eventId })
      },
  [actions.replaceFileInGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { file, fileToReplaceId, requiredPlacementType } = action.payload
        const state = getState()

        // Find file to replace
        const fileToReplace = state.media.find((x) => x.id === fileToReplaceId)
        if (!fileToReplace) throw new Error('File to replace not found')

        // Store old file for potential restoration
        dispatch({ type: actions.startReplace, payload: fileToReplace })

        const [validatedFile, error] = await validateFileAsync(file)
        if (error) {
          // Restore old file on validation error
          dispatch({ type: actions.resetReplace, payload: { error } })
          return
        }

        // Create file object (this will calculate placement_type)
        const fileToUpload = await constructFileObject(validatedFile, error)

        if (fileToUpload.placement_type !== requiredPlacementType) {
          dispatch({ type: actions.resetReplace, payload: { error: `File aspect ratio doesn't match required placement type. Expected: ${requiredPlacementType}, got: ${fileToUpload.placement_type}` } })
          return
        }

        dispatch({
          type: actions.replaceFile,
          payload: buildReplaceFilePayload(fileToUpload, fileToReplace),
        })

        try {
          // Upload the new file
          await uploadFile(dispatch, fileToUpload, { tc: state.tc, tcRunId: state.tcRunId, eventId: state.eventId })
        } catch (error) {
          // If upload throws, restore old file and surface error
          dispatch({
            type: actions.resetReplace,
            payload: { error: error?.message || 'File upload failed' },
          })
          return
        }

    
      },
  [actions.removeGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { groupId } = action.payload
        const state = getState()

        const filesInGroup = state.media.filter(
          (file) =>
            file.placement_group_id === groupId ||
            file.sub_group_id === groupId,
        )

        filesInGroup.forEach((file) => {
          abortUpload(file)
        })

        dispatch({ type: actions.removeFiles, payload: filesInGroup })
      },
  [actions.removeFromGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { fileId } = action.payload
        const state = getState()

        const fileToRemove = state.media.find((x) => x.id === fileId)
        if (!fileToRemove) throw new Error('File not found')

        if (!fileToRemove.placement_group_id && !fileToRemove.sub_group_id) {
          return dispatch({ type: actions.removeFileAsync, payload: fileToRemove })
        }

        abortUpload(fileToRemove)
        dispatch({ type: actions.removeFile, payload: fileToRemove })
      },
  [actions.removeMediaGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { groupId } = action.payload
        const state = getState()
        const filesInGroup = state.media.filter((file) => isFileInCoCreateGroup(file, groupId))

        filesInGroup.forEach((file) => {
          abortUpload(file)
        })

        dispatch({ type: actions.removeMediaGroup, payload: { groupId } })
      },
  [actions.removeFileFromCoCreateGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { fileId } = action.payload
        const state = getState()
        const fileToRemove = state.media.find((x) => x.id === fileId)

        if (!fileToRemove) {
          throw new Error('File not found')
        }

        const groupId = fileToRemove.sub_group_id
        const remainingInGroup = state.media.filter(
          (file) => isFileInCoCreateGroup(file, groupId) && file.id !== fileId,
        )

        abortUpload(fileToRemove)

        if (remainingInGroup.length === 0) {
          dispatch({ type: actions.removeMediaGroup, payload: { groupId } })
        } else {
          dispatch({ type: actions.removeFile, payload: fileToRemove })
        }
      },
  [actions.addFileToCoCreateGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { file, groupId, requiredRatio, originalId: providedOriginalId } = action.payload
        const state = getState()
        const groupImages = state.media.filter((item) => isFileInCoCreateGroup(item, groupId))

        const [validatedFile, validationError] = await validateFileAsync(file)
        if (validationError) {
          throw new Error(validationError)
        }

        const ratioError = await validateCoCreateSlotRatio(validatedFile, requiredRatio)
        if (ratioError) {
          throw new Error(ratioError)
        }

        const fileToUpload = await constructFileObject(validatedFile, null)

        if (!groupImages.length) {
          if (!providedOriginalId) {
            throw new Error('CoCreate group not found')
          }

          const original = state.media.find((item) => item.id === providedOriginalId)

          if (!original) {
            throw new Error('CoCreate group not found')
          }

          fileToUpload.sub_group_id = original.sub_group_id ?? groupId
          fileToUpload.headline = null
          fileToUpload.is_generated = false

          dispatch({ type: actions.addFile, payload: [fileToUpload] })
          await uploadFile(dispatch, fileToUpload, { tc: state.tc, tcRunId: state.tcRunId, eventId: state.eventId })
          return
        }

        const anchorFile = groupImages[0]

        if (groupImages.some((item) => resolveImageSlotRatio(item) === requiredRatio)) {
          throw new Error(`Slot ${requiredRatio} is already filled`)
        }

        fileToUpload.sub_group_id = anchorFile.sub_group_id ?? groupId
        fileToUpload.headline = anchorFile.headline ?? null
        fileToUpload.is_generated = false

        dispatch({ type: actions.addFile, payload: [fileToUpload] })
        await uploadFile(dispatch, fileToUpload, { tc: state.tc, tcRunId: state.tcRunId, eventId: state.eventId })
      },
  [actions.replaceFileInCoCreateGroupAsync]:
    ({ dispatch, getState }) =>
      async (action) => {
        const { file, fileToReplaceId, requiredRatio } = action.payload
        const state = getState()
        const fileToReplace = state.media.find((x) => x.id === fileToReplaceId)

        if (!fileToReplace) {
          throw new Error('File to replace not found')
        }

        const [validatedFile, validationError] = await validateFileAsync(file)
        if (validationError) {
          throw new Error(validationError)
        }

        const ratioError = await validateCoCreateSlotRatio(validatedFile, requiredRatio)
        if (ratioError) {
          throw new Error(ratioError)
        }

        dispatch({ type: actions.startReplace, payload: fileToReplace })

        const fileToUpload = await constructFileObject(validatedFile, null)

        const preparedFile = {
          ...fileToUpload,
          sub_group_id: fileToReplace.sub_group_id ?? null,
          headline: null,
          is_generated: false,
        }

        dispatch({
          type: actions.replaceFile,
          payload: buildReplaceFilePayload(preparedFile, fileToReplace),
        })

        try {
          await uploadFile(dispatch, preparedFile, { tc: state.tc, tcRunId: state.tcRunId, eventId: state.eventId })
        } catch (error) {
          dispatch({
            type: actions.resetReplace,
            payload: { error: error?.message || 'File upload failed' },
          })
        }
      },
}

const abortUpload = (file) => {
  if (file.status === fileStatus.uploading) {
    file.cancelTokenSource.cancel()
    abortMultipartUpload(
      {
        upload_id: file.uploadId,
        object_key: file.objectKey,
      },
      { upload_type: 'media' },
    )
  }
}

const uploadFile = async (dispatch, file, campaignContext) => {
  if (file.status === fileStatus.new) {
    await uploadNewFile(dispatch, file, campaignContext)
  } else if (file.status === fileStatus.uploadFailed) {
    const {
      content: { isValid },
    } = await validateUploadId(
      {
        upload_id: file.uploadId,
        object_key: file.objectKey,
        expected_total_parts: file.expectedTotalParts,
      },
      { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
    )
    if (!isValid) await uploadNewFile(dispatch, file, campaignContext)
    else {
      const presignedUrls = await getPresignedUrls(dispatch, file)
      await uploadParts(dispatch, file, presignedUrls, campaignContext)
    }
  }
}

const uploadNewFile = async (dispatch, file, campaignContext) => {
  const initialUploadData = await initiateUpload(dispatch, file)

  if (!initialUploadData) return

  const newFile = { ...file, ...initialUploadData }
  const presignedUrls = await getPresignedUrls(dispatch, newFile)

  await uploadParts(dispatch, newFile, presignedUrls, campaignContext)
}

const initiateUpload = async (dispatch, file) => {
  let { content: initialUploadData, error } = await initiateMultipartUpload(
    {
      object_name: file.name,
      content_type: file.type,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  if (error) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    return
  }

  initialUploadData = {
    uploadId: initialUploadData.upload_id,
    objectKey: initialUploadData.object_key,
  }

  dispatch({ type: actions.fileUploadInitiate, payload: { ...initialUploadData, id: file.id } })

  return initialUploadData
}

const getPresignedUrls = async (dispatch, file) => {
  const { content: presignedUrls, error } = await createPresignedUrl(
    {
      upload_id: file.uploadId,
      object_key: file.objectKey,
      start_part_number: file.totalUploadedParts + 1,
      total_parts: file.expectedTotalParts,
      total_uploaded_parts: file.totalUploadedParts,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  if (error) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    return
  }

  return presignedUrls
}

const uploadParts = async (dispatch, file, presignedUrls, campaignContext) => {
  let totalUploadedParts = file.totalUploadedParts
  let expectedTotalParts = file.expectedTotalParts
  let partsData = file.partsData

  if (!presignedUrls) return

  for (let index = 0; index < expectedTotalParts; index++) {
    const offset = index * PART_SIZE
    const part = file.file.slice(offset, offset + PART_SIZE)
    const presignedUrl = presignedUrls[index] || ''
    let lastPercentCompleted = 0
    const {
      content: partUploadResult,
      success,
      error,
    } = await callPresignedUrl(presignedUrl, part, { cancelToken: file.cancelTokenSource.token }, (progress) => {
      if (progress > lastPercentCompleted) {
        lastPercentCompleted = progress
        dispatch({ type: actions.editFileInfo, payload: { id: file.id, progress } })
      }
    })

    if (success) {
      partsData = [...partsData, { part_number: Number(index) + 1, etag: partUploadResult.etag }]
      totalUploadedParts = totalUploadedParts + 1

      dispatch({ type: actions.editFileInfo, payload: { id: file.id, partsData, totalUploadedParts } })
    }

    if (error) {
      dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
    }
  }

  if (totalUploadedParts !== expectedTotalParts) return

  const {
    success: uploadCompleted,
    error: uploadCompletionFailed,
    content,
  } = await completeMultipartUpload(
    {
      upload_id: file.uploadId,
      object_key: file.objectKey,
      parts: partsData,
    },
    { cancelToken: file.cancelTokenSource.token, upload_type: 'media' },
  )

  dispatch({ type: actions.editFileInfo, payload: { id: file.id, url: content.location } })

  if (uploadCompleted) {
    dispatch({ type: actions.fileUploadSuccess, payload: { id: file.id } })

    saveLibraryImages({
      filename: file.name,
      url: content.location,
      size: file.size,
      height: file.height,
      width: file.width,
      original: true,
      ratio: file.ratio,
      group_id: file.id,
      eid: campaignContext?.eventId,
      tc: campaignContext?.tc,
      tc_run_id: campaignContext?.tcRunId,
    })
  }
  if (uploadCompletionFailed) {
    dispatch({ type: actions.fileUploadFail, payload: { id: file.id } })
  }
}

const constructFileObject = async (file, error = null) => {
  let expectedTotalParts = Math.ceil(file.size / PART_SIZE, PART_SIZE)

  const dimensions = await getMediaDimensions(file)
  const fileName = file.path || file.name
  
  const placement_type = inferPlacementType(dimensions)
  const ratio = computeAspectRatio(dimensions.width, dimensions.height)

  return {
    file,
    filename: fileName,
    path: fileName,
    name: file.name,
    type: file.type,
    size: file.size,
    height: dimensions.height,
    width: dimensions.width,
    ratio,
    headline: null,
    is_generated: false,
    generating: false,
    sub_group_id: v4(),
    placement_type,
    id: v4(),
    error: error ? error : null,
    partsData: [],
    expectedTotalParts: expectedTotalParts,
    totalUploadedParts: 0,
    uploadId: null,
    objectKey: null,
    cancelTokenSource: axios.CancelToken.source(),
    progress: 0,
    preview: URL.createObjectURL(file),
    url: null,
    status: error ? fileStatus.invalid : fileStatus.new,
  }
}

const constructEditObject = (media, payload) => {
  return media.map((file) => {
    if (file.id === payload.id && payload.progress) {
      const progress = Math.round((1 / file.expectedTotalParts) * (file.totalUploadedParts * 100 + payload.progress))
      payload = { ...payload, progress }
    }
    if (file.id === payload.id) {
      return { ...file, ...payload }
    }

    return file
  })
}

async function validateCoCreateSlotRatio(file, requiredRatio) {
  if (!file.type?.startsWith('image/') && !file.type?.startsWith('video/')) {
    return i18n.t('common.errors.fileFormat')
  }

  const dimensions = await getMediaDimensions(file)
  const ratio = computeAspectRatio(dimensions.width, dimensions.height)

  if (!validatePlacementForSlot(dimensions.width, dimensions.height, requiredRatio)) {
    return i18n.t('Recommendations.campaign.setup.media.errors.image.ratioSlot', {
      fileName: file.name,
      requiredRatio,
      uploadedRatio: ratio,
    })
  }

  return null
}

async function validateFileAsync(file) {
  const fileSizeMB = Math.round(file.size / 1000 / 1000)
  let error

  if (file.type.startsWith('image')) {
    if (fileSizeMB > constants.MAX_IMG_UPLOAD_SIZE) {
      error = i18n.t('common.errors.imgSize', { size: constants.MAX_IMG_UPLOAD_SIZE.toFixed(1) })
    }
  } else if (file.type.startsWith('video')) {
    const video = document.createElement('video')
    video.src = window.URL.createObjectURL(file)

    await new Promise((resolve) => {
      video.addEventListener('loadedmetadata', () => {
        window.URL.revokeObjectURL(video.src)
        resolve()
      })
    })

    if (fileSizeMB > constants.MAX_VIDEO_UPLOAD_SIZE) {
      error = i18n.t('common.errors.videoSize', { size: constants.MAX_VIDEO_UPLOAD_SIZE.toFixed(1) / 1000 })
    }
  } else {
    error = i18n.t('common.errors.fileFormat')
  }

  return [file, error]
}

export function allFilesUploaded(media) {
  return media.every((x) => x.status === fileStatus.success)
}
